import type { ReactNode } from 'react';
import type { Opportunity, ContactSummary } from '../../../types';
import { getDaysInStage, formatCurrency } from '../../../hooks/usePipelineData';
import styles from './DealCard.module.css';

interface QuickActionsProps {
  onView: () => void;
  onEdit: () => void;
}

function QuickActions({ onView, onEdit }: QuickActionsProps): ReactNode {
  return (
    <div className={styles.quickActions}>
      <button
        className={styles.actionButton}
        onClick={(e) => { e.stopPropagation(); onView(); }}
        title="View deal"
        aria-label="View deal"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
        </svg>
      </button>
      <button
        className={styles.actionButton}
        onClick={(e) => { e.stopPropagation(); onEdit(); }}
        title="Edit deal"
        aria-label="Edit deal"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
        </svg>
      </button>
    </div>
  );
}

interface DealCardProps {
  opportunity: Opportunity;
  contact?: ContactSummary;
  stageColor: string;
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  isDragging?: boolean;
}

export function DealCard({
  opportunity,
  contact,
  stageColor,
  onView,
  onEdit,
  isDragging = false,
}: DealCardProps): ReactNode {
  const daysInStage = getDaysInStage(opportunity);
  const isOverdue = daysInStage > 14; // Consider deals stale after 2 weeks

  return (
    <div
      className={`${styles.card} ${isDragging ? styles.dragging : ''}`}
      style={{ '--stage-color': stageColor } as React.CSSProperties}
    >
      <div className={styles.colorBar} />
      
      <div className={styles.content}>
        <div className={styles.header}>
          <h3 className={styles.name}>{opportunity.name}</h3>
          <QuickActions
            onView={() => onView(opportunity.id)}
            onEdit={() => onEdit(opportunity.id)}
          />
        </div>

        <div className={styles.value}>
          {formatCurrency(opportunity.amount, opportunity.currency)}
        </div>

        {contact && (
          <div className={styles.contact}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className={styles.contactIcon}>
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
            </svg>
            <span className={styles.contactName}>
              {contact.name}
              {contact.company && <span className={styles.company}> • {contact.company}</span>}
            </span>
          </div>
        )}

        <div className={styles.footer}>
          <span className={`${styles.daysInStage} ${isOverdue ? styles.overdue : ''}`}>
            {daysInStage === 0 ? 'Today' : `${daysInStage}d in stage`}
          </span>
          <span className={styles.probability}>{opportunity.probability}%</span>
        </div>
      </div>
    </div>
  );
}
